/**
 * @module ui.scroll
 * @version 4.9.0
 * @description LIFTR Scroll Web Component - Performanter Scroll-Container mit Progress,
 * optionalen Controls, reduced-motion Aware Smooth-Scroll und Keyboard-Navigation.
 * 
 * @element liftr-scroll
 * 
 * @fires liftr:scroll:state - Scroll state changed (active, canPrev, canNext, progress)
 * 
 * @listens liftr:panel:opened - Recalculates scroll when panel opens in dialog
 * 
 * @attr {string} liftr-id - Optional identifier
 * @attr {string} liftr-axis - Scroll direction: x | y (required)
 * @attr {string} liftr-mask - Declarative mask: left | right | top | bottom
 * @attr {string} liftr-state - State output: active, can-prev, can-next, disabled
 * @attr {string} liftr-nav - Navigation output
 * 
 * @changelog
 * - 4.9.0: REFACTOR: Extract shared #computeScrollState() pure function
 *        - Replaces 3 inline scroll-state computations in mount(), #enableScroll(), #scheduleUpdate()
 *        - Zero DOM reads/writes — only reads this.config.edgeThreshold and this.#axis
 *        - Read/Write phase separation in call sites preserved
 *        - Mask computation in #scheduleUpdate() stays inline (only used there)
 * - 4.8.0: PERF: Inline Read/Compute/Write batch in #scheduleUpdate()
 *        - Eliminates forced reflow from Read>Write>Read pattern on hot path
 *        - #recalc() and #updateState() unchanged (used by mount/enable/scroll)
 *        - #updateMask() inlined for scheduleUpdate path (avoids redundant scrollLeft/scrollTop reads)
 * - 4.7.0: PERF: Batched geometry reads/writes in mount() and #enableScroll()
 *        - Eliminates 2 forced reflow cycles per instance (3→1)
 *        - ADD: #measureMinHeight() pure-read helper
 *        - REMOVE: #setInitialMinHeight() (replaced by inline read/write split)
 * - 4.6.1: AUDIT FIX - Added LISTEN constant for liftr:panel:opened (v4.5 compliance)
 * - 4.6.0: PUBLIC API COMPLIANCE
 *        - ADD: scrollPrev() method
 *        - ADD: scrollNext() method
 *        - ADD: scrollToStart() method
 *        - ADD: scrollToEnd() method
 *        - ADD: scrollTo(position) method
 *        - ADD: get progress getter
 *        - ADD: get canPrev getter
 *        - ADD: get canNext getter
 *        - ADD: get isActive getter
 *        - ADD: get isDisabled getter
 * - 4.5.0: AUDIT COMPLIANCE - Quality Checks v4.5
 *        - ROLE → ROLES (frozen)
 *        - CSS_VARS → PROPERTIES (frozen)
 *        - Added STATES, EVENTS, SETTINGS constants
 *        - Removed liftr-scroll-progress attribute (use CSS vars)
 *        - Correct export pattern
 * - 4.1.1: FIX: Force layout reflow in #recalc() before measuring dimensions
 *          Fixes timing issues when scroll is inside a panel that just opened
 * - 4.1.0: Removed dead code (NAV_DEFAULTS, NAV_VALID, readNavSettings), using NAV.* from core.liftr.js
 * - 4.0.0: Initial v4.0 implementation
 * 
 * @example
 * <liftr-scroll liftr-axis="x">
 *   <div liftr-role="scroll-viewport">
 *     <div liftr-role="scroll-panel" liftr-mask="right">
 *       <div liftr-role="scroll-content">...</div>
 *     </div>
 *   </div>
 * </liftr-scroll>
 */

import { LiftrModule } from './core.module.js';
import { ATTRIBUTES, i18n, NAV } from './core.liftr.js';
import { throttle, observeResize, on, once, prefersReducedMotion } from './core.utilities.js';

/* ═══════════════════════════════════════════════════════════════════
 * MODULE CONSTANTS
 * ═══════════════════════════════════════════════════════════════════ */

/**
 * Role constants for DOM queries.
 * @type {Readonly<Record<string, string>>}
 */
const ROLES = Object.freeze({
  VIEWPORT: 'scroll-viewport',
  PANEL:    'scroll-panel',
  CONTENT:  'scroll-content',
  PREV:     'scroll-toggle-prev',
  NEXT:     'scroll-toggle-next',
  START:    'scroll-toggle-start',
  END:      'scroll-toggle-end'
});

/**
 * State tokens for liftr-state attribute.
 * @type {Readonly<Record<string, string>>}
 */
const STATES = Object.freeze({
  ACTIVE:   'active',
  CAN_PREV: 'can-prev',
  CAN_NEXT: 'can-next'
});

/**
 * Event action names (auto-prefixed with liftr:scroll:).
 * @type {Readonly<Record<string, string>>}
 */
const EVENTS = Object.freeze({
  STATE: 'state'
});

/**
 * Settings tokens for liftr-settings attribute.
 * @type {Readonly<Record<string, string>>}
 */
const SETTINGS = Object.freeze({
  // Reserved for future use
});

/**
 * CSS Custom Properties set by JavaScript.
 * @type {Readonly<Record<string, string>>}
 */
const PROPERTIES = Object.freeze({
  PROGRESS_X: '--scroll-progress-x',
  PROGRESS_Y: '--scroll-progress-y'
});

/**
 * External events this module listens to.
 * Full event strings — used in on() calls.
 * NOT exported — internal to module only.
 * @private
 */
const LISTEN = Object.freeze({
  PANEL_OPENED: 'liftr:panel:opened'
});

const PERFORMANCE = {
  scrollThrottleMs: 32
};

/* ═══════════════════════════════════════════════════════════════════
 * LIFTR SCROLL WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

class LiftrScroll extends LiftrModule {
  
  /* ═══════════════════════════════════════════════════════════════════
   * STATIC PROPERTIES
   * ═══════════════════════════════════════════════════════════════════ */
  
  static NAME = 'scroll';
  
  static DEFAULTS = {
    enabled: true,
    pageFactor: 0.9,
    stepPx: null,
    smooth: true,
    observeResize: true,
    edgeThreshold: 2,
    wheelLock: 'none',
    activateOnOverflow: true
  };
  
  /* ═══════════════════════════════════════════════════════════════════
   * INSTANCE PROPERTIES
   * ═══════════════════════════════════════════════════════════════════ */
  
  // Elements
  #viewport = null;
  #panel = null;
  #content = null;
  
  // Buttons
  #btnPrev = null;
  #btnNext = null;
  #btnStart = null;
  #btnEnd = null;
  
  // Mask manager for viewport
  #viewportMask = null;
  
  // State
  #axis = 'x';
  #maxX = 0;
  #maxY = 0;
  #isDisabled = false;
  #isBound = false;
  #navSettings = null;
  #watcher = null;
  
  // Labels
  #labels = null;
  
  // Change tracking
  #lastState = {
    active: null,
    canPrev: null,
    canNext: null,
    progress: null,
    rootId: null
  };
  
  // RAF scheduling
  #scheduled = false;
  
  // Reduced motion preference
  #prefersReduced = false;
  
  /* ═══════════════════════════════════════════════════════════════════
   * LIFECYCLE
   * ═══════════════════════════════════════════════════════════════════ */
  
  mount() {
    // Query elements
    this.#viewport = this.role(ROLES.VIEWPORT);
    this.#panel = this.role(ROLES.PANEL) || this;
    this.#content = this.role(ROLES.CONTENT) || null;
    
    if (!this.#panel) {
      throw new Error('Scroll panel not found');
    }
    
    // Read axis
    this.#axis = this.getAttribute(ATTRIBUTES.AXIS) || 'x';
    if (this.#axis !== 'x' && this.#axis !== 'y') {
      throw new Error(`Invalid liftr-axis value: "${this.#axis}" (expected "x" or "y")`);
    }
    
    // I18N labels
    this.#labels = i18n.get('scroll') || {
      prev: 'Previous',
      next: 'Next',
      start: 'Start',
      end: 'End'
    };
    
    // Reduced motion preference
    this.#prefersReduced = prefersReducedMotion();
    
    // Create mask manager for viewport (if viewport exists and has mask attribute)
    if (this.#viewport) {
      this.#viewportMask = this.createMask(this.#viewport);
    }
    
    // Setup unified watcher
    this.#watcher = this.watchSettings('scroll', {
      enabled: '--scroll-enabled',
      nav: true,
      resize: true,
      debounce: 100
    }, {
      onEnable: (s) => this.#enableScroll(s),
      onDisable: () => this.#disableScroll(),
      onChange: (changes) => this.#handleChanges(changes)
    });
    
    this.#isDisabled = !this.#watcher.isEnabled();
    
    // Initial setup (only if enabled)
    if (!this.#isDisabled) {
      if (this.#navSettings?.mode !== 'false') {
        this.#setupNavigation();
      }
      
      if (this.config.observeResize) {
        const targets = [this.#panel, ...(this.#content ? [this.#content] : [])];
        const disconnect = observeResize(targets, () => this.#scheduleUpdate());
        this.addDisposable(disconnect);
      }
      
      // ═══════════════════════════════════════════════════════════════
      // BATCHED GEOMETRY: All reads first, then all writes
      // ═══════════════════════════════════════════════════════════════

      // READ PHASE (1 Reflow — after nav button insertion)
      const minHeight = this.#measureMinHeight();
      const maxX = Math.max(0, this.#panel.scrollWidth - this.#panel.clientWidth);
      const maxY = Math.max(0, this.#panel.scrollHeight - this.#panel.clientHeight);
      const x = this.#panel.scrollLeft;
      const y = this.#panel.scrollTop;

      // COMPUTE PHASE
      this.#maxX = maxX;
      this.#maxY = maxY;
      const s = this.#computeScrollState({ maxX, maxY, x, y });

      // WRITE PHASE (no further reflow)
      if (minHeight > 0) {
        this.style.minHeight = `${minHeight}px`;
      }

      if (s.canScroll) {
        this.#bindActiveBits();
      } else if (this.config.activateOnOverflow) {
        this.state.remove(STATES.ACTIVE);
        this.state.remove(STATES.CAN_PREV);
        this.state.remove(STATES.CAN_NEXT);
      }

      this.#panel.style.setProperty(PROPERTIES.PROGRESS_X, String(s.pxX));
      this.#panel.style.setProperty(PROPERTIES.PROGRESS_Y, String(s.pxY));

      this.state.toggle(STATES.ACTIVE, s.active);
      this.state.toggle(STATES.CAN_PREV, s.canPrev);
      this.state.toggle(STATES.CAN_NEXT, s.canNext);

      this.#updateA11y(s.canPrev, s.canNext);
      this.#updateMask();
      this.#publishState(s.active, s.canPrev, s.canNext, Number(s.prog.toFixed(4)));

      // ═══════════════════════════════════════════════════════════════

      this.addDisposable(
        on(LISTEN.PANEL_OPENED, () => {
          if (!this.closest('dialog[open]')) return;
          void this.offsetHeight;
          this.#scheduleUpdate();
        })
      );

      // Load event handler
      const offLoad = once(window, 'load', () => this.#scheduleUpdate());
      this.addDisposable(offLoad);
    }
  }
  
  unmount() {
    this.#watcher?.destroy();
    this.#unbindActiveBits();
    this.#unbindControls();
    this.#removeNavControls();
    
    this.removeAttribute(ATTRIBUTES.NAV);
    
    super.unmount();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * ENABLE / DISABLE
   * ═══════════════════════════════════════════════════════════════════ */
  
  #enableScroll(currentSettings) {
    this.#isDisabled = false;
    this.#navSettings = currentSettings.nav;
    
    if (this.#navSettings) {
      NAV.applyOutput(this, this.#navSettings);
    }
    
    if (this.#navSettings?.mode !== 'false') {
      this.#setupNavigation();
    }
    
    // READ PHASE
    const minHeight = this.#measureMinHeight();
    const maxX = Math.max(0, this.#panel.scrollWidth - this.#panel.clientWidth);
    const maxY = Math.max(0, this.#panel.scrollHeight - this.#panel.clientHeight);
    const x = this.#panel.scrollLeft;
    const y = this.#panel.scrollTop;

    // COMPUTE PHASE
    this.#maxX = maxX;
    this.#maxY = maxY;
    const s = this.#computeScrollState({ maxX, maxY, x, y });

    // WRITE PHASE
    if (minHeight > 0) {
      this.style.minHeight = `${minHeight}px`;
    }

    if (s.canScroll) {
      this.#bindActiveBits();
    } else if (this.config.activateOnOverflow) {
      this.state.remove(STATES.ACTIVE);
      this.state.remove(STATES.CAN_PREV);
      this.state.remove(STATES.CAN_NEXT);
    }

    this.#panel.style.setProperty(PROPERTIES.PROGRESS_X, String(s.pxX));
    this.#panel.style.setProperty(PROPERTIES.PROGRESS_Y, String(s.pxY));

    this.state.toggle(STATES.ACTIVE, s.active);
    this.state.toggle(STATES.CAN_PREV, s.canPrev);
    this.state.toggle(STATES.CAN_NEXT, s.canNext);

    this.#updateA11y(s.canPrev, s.canNext);
    this.#updateMask();
    this.#publishState(s.active, s.canPrev, s.canNext, Number(s.prog.toFixed(4)));
    
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#scheduleUpdate());
    });
  }
  
  #disableScroll() {
    this.#isDisabled = true;
    
    this.#unbindActiveBits();
    this.#unbindControls();
    this.#removeNavControls();
    
    this.removeAttribute(ATTRIBUTES.NAV);
    this.state.clear();
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * SETTINGS CHANGE HANDLER
   * ═══════════════════════════════════════════════════════════════════ */
  
  #handleChanges(changes) {
    if (this.#isDisabled) return;
    
    if (changes.initial) {
      this.#navSettings = changes.nav;
      if (this.#navSettings) {
        NAV.applyOutput(this, this.#navSettings);
      }
      return;
    }
    
    if (changes.nav) {
      const oldMode = this.#navSettings?.mode;
      const newMode = changes.nav?.mode;
      
      if (oldMode !== newMode) {
        this.#unbindControls();
        this.#removeNavControls();
        this.#navSettings = changes.nav;
        NAV.applyOutput(this, this.#navSettings);
        
        if (this.#navSettings.mode !== 'false') {
          this.#setupNavigation();
        }
      } else {
        this.#navSettings = changes.nav;
        NAV.applyOutput(this, this.#navSettings);
      }
    }
    
    if (changes.size) {
      this.#scheduleUpdate();
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * SCROLL STATE
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Pure scroll-state computation — no DOM reads, no DOM writes.
   * Reads only this.config.edgeThreshold and this.#axis (instance state).
   * @param {Object} dims - Geometry dimensions from DOM read phase
   * @param {number} dims.maxX - Maximum horizontal scroll (scrollWidth - clientWidth)
   * @param {number} dims.maxY - Maximum vertical scroll (scrollHeight - clientHeight)
   * @param {number} dims.x - Current scrollLeft
   * @param {number} dims.y - Current scrollTop
   * @returns {{ canScroll: boolean, active: boolean, canPrev: boolean, canNext: boolean, pxX: number, pxY: number, prog: number }}
   */
  #computeScrollState({ maxX, maxY, x, y }) {
    const threshold = this.config.edgeThreshold;
    const isY = this.#axis === 'y';
    
    const canScroll = isY
      ? maxY > threshold
      : maxX > threshold;
    
    const pxX = maxX ? Math.min(1, Math.max(0, x / maxX)) : 0;
    const pxY = maxY ? Math.min(1, Math.max(0, y / maxY)) : 0;
    const prog = isY ? pxY : pxX;
    
    const canPrev = isY
      ? y > threshold
      : x > threshold;
    
    const canNext = isY
      ? y < maxY - threshold
      : x < maxX - threshold;
    
    const active = maxX > threshold || maxY > threshold;
    
    return { canScroll, active, canPrev, canNext, pxX, pxY, prog };
  }
  
  #scheduleUpdate() {
    if (this.#scheduled || this.#isDisabled) return;
    this.#scheduled = true;
    
    requestAnimationFrame(() => {
      this.#scheduled = false;
      if (this.#isDisabled) return;
      
      // ════════════════════════════════════════════════════════════
      // READ PHASE — all geometry reads, zero writes
      // ════════════════════════════════════════════════════════════
      
      const maxX = Math.max(0, this.#panel.scrollWidth - this.#panel.clientWidth);
      const maxY = Math.max(0, this.#panel.scrollHeight - this.#panel.clientHeight);
      const x = this.#panel.scrollLeft;
      const y = this.#panel.scrollTop;
      
      // ════════════════════════════════════════════════════════════
      // COMPUTE PHASE — pure logic, no DOM access
      // ════════════════════════════════════════════════════════════
      
      this.#maxX = maxX;
      this.#maxY = maxY;
      
      const s = this.#computeScrollState({ maxX, maxY, x, y });
      
      // Mask directions (computed, not yet written)
      let maskUpdates = null;
      if (this.#viewportMask?.has()) {
        if (this.#axis === 'x' && maxX > this.config.edgeThreshold) {
          maskUpdates = {
            left:  s.canPrev,
            right: s.canNext
          };
        } else if (this.#axis === 'y' && maxY > this.config.edgeThreshold) {
          maskUpdates = {
            top:    s.canPrev,
            bottom: s.canNext
          };
        }
      }
      
      // ════════════════════════════════════════════════════════════
      // WRITE PHASE — all DOM writes, zero reads
      // ════════════════════════════════════════════════════════════
      
      if (s.canScroll) {
        this.#bindActiveBits();
      } else if (this.config.activateOnOverflow) {
        this.state.remove(STATES.ACTIVE);
        this.state.remove(STATES.CAN_PREV);
        this.state.remove(STATES.CAN_NEXT);
      }
      
      this.#panel.style.setProperty(PROPERTIES.PROGRESS_X, String(s.pxX));
      this.#panel.style.setProperty(PROPERTIES.PROGRESS_Y, String(s.pxY));
      
      this.state.toggle(STATES.ACTIVE, s.active);
      this.state.toggle(STATES.CAN_PREV, s.canPrev);
      this.state.toggle(STATES.CAN_NEXT, s.canNext);
      
      this.#updateA11y(s.canPrev, s.canNext);
      
      // Mask writes (from pre-computed values)
      if (maskUpdates) {
        if (this.#axis === 'x') {
          this.#viewportMask.toggle('left', maskUpdates.left);
          this.#viewportMask.toggle('right', maskUpdates.right);
        } else {
          this.#viewportMask.toggle('top', maskUpdates.top);
          this.#viewportMask.toggle('bottom', maskUpdates.bottom);
        }
      }
      
      this.#publishState(s.active, s.canPrev, s.canNext, Number(s.prog.toFixed(4)));
    });
  }
  
  #recalc() {
    if (this.#isDisabled) return;
    
    this.#maxX = Math.max(0, this.#panel.scrollWidth - this.#panel.clientWidth);
    this.#maxY = Math.max(0, this.#panel.scrollHeight - this.#panel.clientHeight);
    
    const canScroll = this.#axis === 'x'
      ? this.#maxX > this.config.edgeThreshold
      : this.#maxY > this.config.edgeThreshold;
    
    if (canScroll) {
      this.#bindActiveBits();
    } else if (this.config.activateOnOverflow) {
      this.state.remove(STATES.ACTIVE);
      this.state.remove(STATES.CAN_PREV);
      this.state.remove(STATES.CAN_NEXT);
    }
  }
  
  #updateState() {
    const x = this.#panel.scrollLeft;
    const y = this.#panel.scrollTop;
    
    const pxX = this.#maxX ? Math.min(1, Math.max(0, x / this.#maxX)) : 0;
    const pxY = this.#maxY ? Math.min(1, Math.max(0, y / this.#maxY)) : 0;
    
    this.#panel.style.setProperty(PROPERTIES.PROGRESS_X, String(pxX));
    this.#panel.style.setProperty(PROPERTIES.PROGRESS_Y, String(pxY));
    
    const prog = this.#axis === 'y' ? pxY : pxX;
    
    const canPrev = this.#axis === 'y'
      ? y > this.config.edgeThreshold
      : x > this.config.edgeThreshold;
      
    const canNext = this.#axis === 'y'
      ? y < this.#maxY - this.config.edgeThreshold
      : x < this.#maxX - this.config.edgeThreshold;
    
    const active = this.#maxX > this.config.edgeThreshold || this.#maxY > this.config.edgeThreshold;
    
    this.state.toggle(STATES.ACTIVE, active);
    this.state.toggle(STATES.CAN_PREV, canPrev);
    this.state.toggle(STATES.CAN_NEXT, canNext);
    
    this.#updateA11y(canPrev, canNext);
    this.#updateMask();
    this.#publishState(active, canPrev, canNext, Number(prog.toFixed(4)));
  }
  
  #publishState(active, canPrev, canNext, progress) {
    const rootId = this.id || this.getAttribute(ATTRIBUTES.ID) || null;
    
    const changed =
      this.#lastState.active !== active ||
      this.#lastState.canPrev !== canPrev ||
      this.#lastState.canNext !== canNext ||
      this.#lastState.progress !== progress ||
      this.#lastState.rootId !== rootId;
    
    if (!changed) return;
    
    this.#lastState = { active, canPrev, canNext, progress, rootId };
    
    this.emit(EVENTS.STATE, {
      axis: this.#axis,
      active,
      canPrev,
      canNext,
      progress
    });
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * MASK
   * ═══════════════════════════════════════════════════════════════════ */
  
  #updateMask() {
    if (!this.#viewportMask || !this.#viewportMask.has()) return;
    
    if (this.#axis === 'x') {
      if (this.#maxX <= this.config.edgeThreshold) return;
      this.#viewportMask.toggle('left', this.#panel.scrollLeft > this.config.edgeThreshold);
      this.#viewportMask.toggle('right', this.#panel.scrollLeft < this.#maxX - this.config.edgeThreshold);
    } else {
      if (this.#maxY <= this.config.edgeThreshold) return;
      this.#viewportMask.toggle('top', this.#panel.scrollTop > this.config.edgeThreshold);
      this.#viewportMask.toggle('bottom', this.#panel.scrollTop < this.#maxY - this.config.edgeThreshold);
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * ACCESSIBILITY
   * ═══════════════════════════════════════════════════════════════════ */
  
  #updateA11y(canPrev, canNext) {
    if (this.#btnPrev) this.#btnPrev.setAttribute('aria-disabled', canPrev ? 'false' : 'true');
    if (this.#btnNext) this.#btnNext.setAttribute('aria-disabled', canNext ? 'false' : 'true');
    if (this.#btnStart) this.#btnStart.setAttribute('aria-disabled', canPrev ? 'false' : 'true');
    if (this.#btnEnd) this.#btnEnd.setAttribute('aria-disabled', canNext ? 'false' : 'true');
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * SCROLLING (Internal)
   * ═══════════════════════════════════════════════════════════════════ */
  
  #behavior() {
    return this.config.smooth && !this.#prefersReduced ? 'smooth' : 'auto';
  }
  
  #stepDistance() {
    if (Number.isFinite(this.config.stepPx) && this.config.stepPx > 0) {
      return this.config.stepPx;
    }
    const size = this.#axis === 'y' ? this.#panel.clientHeight : this.#panel.clientWidth;
    return Math.max(1, Math.round(size * (Number(this.config.pageFactor) || 0.9)));
  }
  
  #scrollByStep(direction) {
    if (this.#isDisabled) return;
    
    const dist = this.#stepDistance();
    const b = this.#behavior();
    
    if (this.#axis === 'y') {
      if (direction === 'prev') this.#panel.scrollBy({ top: -dist, behavior: b });
      else if (direction === 'next') this.#panel.scrollBy({ top: dist, behavior: b });
    } else {
      if (direction === 'prev') this.#panel.scrollBy({ left: -dist, behavior: b });
      else if (direction === 'next') this.#panel.scrollBy({ left: dist, behavior: b });
    }
  }
  
  #scrollToEdge(pos) {
    if (this.#isDisabled) return;
    
    const b = this.#behavior();
    if (this.#axis === 'y') {
      const top = pos === 'start' ? 0 : this.#maxY;
      this.#panel.scrollTo({ top, behavior: b });
    } else {
      const left = pos === 'start' ? 0 : this.#maxX;
      this.#panel.scrollTo({ left, behavior: b });
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * NAVIGATION CONTROLS
   * ═══════════════════════════════════════════════════════════════════ */
  
  #setupNavigation() {
    if (!this.#navSettings || this.#navSettings.mode === 'false') return;
    
    if (this.#navSettings.mode === 'auto') {
      this.#createNavControls();
    } else if (this.#navSettings.mode === 'static') {
      this.#bindStaticControls();
    }
    
    this.#bindControls();
  }
  
  #createNavControls() {
    if (!this.#navSettings || this.#navSettings.mode !== 'auto') return;
    
    this.#btnPrev = NAV.createButton(ROLES.PREV, 'prev', this.#labels?.prev || 'Previous');
    this.#btnNext = NAV.createButton(ROLES.NEXT, 'next', this.#labels?.next || 'Next');
    
    this.appendChild(this.#btnPrev);
    this.appendChild(this.#btnNext);
  }
  
  #bindStaticControls() {
    if (!this.#navSettings || this.#navSettings.mode !== 'static') return;
    
    this.#btnPrev = this.role(ROLES.PREV);
    this.#btnNext = this.role(ROLES.NEXT);
    this.#btnStart = this.role(ROLES.START);
    this.#btnEnd = this.role(ROLES.END);
    
    if (this.#btnPrev) NAV.ensureRoles(this.#btnPrev, 'prev');
    if (this.#btnNext) NAV.ensureRoles(this.#btnNext, 'next');
    if (this.#btnStart) NAV.ensureRoles(this.#btnStart, 'prev');
    if (this.#btnEnd) NAV.ensureRoles(this.#btnEnd, 'next');
  }
  
  #removeNavControls() {
    if (this.#navSettings?.mode === 'auto') {
      this.#btnPrev?.remove();
      this.#btnNext?.remove();
      this.#btnStart?.remove();
      this.#btnEnd?.remove();
    }
    this.#btnPrev = null;
    this.#btnNext = null;
    this.#btnStart = null;
    this.#btnEnd = null;
  }
  
  #bindControls() {
    if (this.#btnPrev) {
      const off = on(this.#btnPrev, 'click', () => this.#scrollByStep('prev'));
      this.addDisposable(off);
    }
    if (this.#btnNext) {
      const off = on(this.#btnNext, 'click', () => this.#scrollByStep('next'));
      this.addDisposable(off);
    }
    if (this.#btnStart) {
      const off = on(this.#btnStart, 'click', () => this.#scrollToEdge('start'));
      this.addDisposable(off);
    }
    if (this.#btnEnd) {
      const off = on(this.#btnEnd, 'click', () => this.#scrollToEdge('end'));
      this.addDisposable(off);
    }
  }
  
  #unbindControls() {
    // Cleanup handled by addDisposable
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * EVENT BINDING
   * ═══════════════════════════════════════════════════════════════════ */
  
  #bindActiveBits() {
    if (this.#isBound || this.#isDisabled) return;
    
    // Scroll listener (throttled)
    const onScrollThrottled = throttle(
      () => this.#updateState(),
      PERFORMANCE.scrollThrottleMs,
      { leading: true, trailing: true }
    );
    const offScroll = on(this.#panel, 'scroll', onScrollThrottled, { passive: true });
    this.addDisposable(() => {
      onScrollThrottled.cancel?.();
      offScroll();
    });
    
    // Resize listener
    const offResize = on(window, 'resize', () => this.#scheduleUpdate());
    this.addDisposable(offResize);
    
    // Wheel lock
    this.#bindWheelLock();
    
    // Keyboard navigation
    const offKey = on(window, 'keydown', (e) => this.#handleKeydown(e));
    this.addDisposable(offKey);
    
    this.#isBound = true;
  }
  
  #unbindActiveBits() {
    // All cleanup handled by addDisposable in unmount
    this.#isBound = false;
  }
  
  #bindWheelLock() {
    if (this.config.wheelLock === 'none') return;
    
    const offWheel = on(this.#panel, 'wheel', (e) => {
      if (e.ctrlKey) return;
      
      if (this.config.wheelLock === 'x' && this.#axis === 'x' &&
          Math.abs(e.deltaY) > Math.abs(e.deltaX) && this.#maxX > 0) {
        e.preventDefault();
        this.#panel.scrollBy({ left: e.deltaY, behavior: this.#behavior() });
      } else if (this.config.wheelLock === 'y' && this.#axis === 'y' &&
                 Math.abs(e.deltaX) > Math.abs(e.deltaY) && this.#maxY > 0) {
        e.preventDefault();
        this.#panel.scrollBy({ top: e.deltaX, behavior: this.#behavior() });
      }
    }, { passive: false });
    
    this.addDisposable(offWheel);
  }
  
  #handleKeydown(e) {
    if (!this.#panel.contains(document.activeElement)) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        if (this.#axis === 'x') { e.preventDefault(); this.#scrollByStep('prev'); }
        break;
      case 'ArrowRight':
        if (this.#axis === 'x') { e.preventDefault(); this.#scrollByStep('next'); }
        break;
      case 'ArrowUp':
        if (this.#axis === 'y') { e.preventDefault(); this.#scrollByStep('prev'); }
        break;
      case 'ArrowDown':
        if (this.#axis === 'y') { e.preventDefault(); this.#scrollByStep('next'); }
        break;
      case 'Home':
        e.preventDefault();
        this.#scrollToEdge('start');
        break;
      case 'End':
        e.preventDefault();
        this.#scrollToEdge('end');
        break;
      case 'PageUp':
        if (this.#axis === 'y') { e.preventDefault(); this.#scrollByStep('prev'); }
        break;
      case 'PageDown':
        if (this.#axis === 'y') { e.preventDefault(); this.#scrollByStep('next'); }
        break;
    }
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * MIN HEIGHT HELPER
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Measure total height of non-scroll children (pure read, no DOM writes).
   * @returns {number} Total height in pixels, 0 if no relevant children
   */
  #measureMinHeight() {
    const otherChildren = Array.from(this.children).filter(child =>
      child !== this.#panel &&
      child !== this.#viewport &&
      !child.getAttribute(ATTRIBUTES.ROLE)?.split(' ').includes(ROLES.PANEL)
    );
    
    let totalHeight = 0;
    
    for (const child of otherChildren) {
      const accordionToggles = child.querySelectorAll(`[${ATTRIBUTES.ROLE}*="accordion-toggle"]`);
      
      if (accordionToggles.length > 0) {
        for (const toggle of accordionToggles) {
          totalHeight += toggle.offsetHeight;
        }
      } else {
        totalHeight += child.offsetHeight;
      }
    }
    
    return totalHeight;
  }
  
  /* ═══════════════════════════════════════════════════════════════════
   * PUBLIC API
   * ═══════════════════════════════════════════════════════════════════ */
  
  /**
   * Scroll to previous page/step
   */
  scrollPrev() {
    this.#scrollByStep('prev');
  }
  
  /**
   * Scroll to next page/step
   */
  scrollNext() {
    this.#scrollByStep('next');
  }
  
  /**
   * Scroll to start (top or left)
   */
  scrollToStart() {
    this.#scrollToEdge('start');
  }
  
  /**
   * Scroll to end (bottom or right)
   */
  scrollToEnd() {
    this.#scrollToEdge('end');
  }
  
  /**
   * Scroll to specific position (0-1 as progress, or pixel value)
   * @param {number} position - Progress (0-1) or pixel value (>1)
   * @param {boolean} [smooth=true] - Use smooth scrolling
   */
  scrollTo(position, smooth = true) {
    if (this.#isDisabled) return;
    
    const behavior = smooth && !this.#prefersReduced ? 'smooth' : 'auto';
    
    // Interpret as progress if 0-1, otherwise as pixels
    let target;
    if (position >= 0 && position <= 1) {
      target = this.#axis === 'y' 
        ? position * this.#maxY 
        : position * this.#maxX;
    } else {
      target = position;
    }
    
    if (this.#axis === 'y') {
      this.#panel.scrollTo({ top: target, behavior });
    } else {
      this.#panel.scrollTo({ left: target, behavior });
    }
  }
  
  /**
   * Current scroll progress (0-1)
   * @type {number}
   */
  get progress() {
    if (this.#isDisabled || !this.#panel) return 0;
    
    const x = this.#panel.scrollLeft;
    const y = this.#panel.scrollTop;
    
    return this.#axis === 'y'
      ? (this.#maxY ? Math.min(1, Math.max(0, y / this.#maxY)) : 0)
      : (this.#maxX ? Math.min(1, Math.max(0, x / this.#maxX)) : 0);
  }
  
  /**
   * Can scroll to previous position
   * @type {boolean}
   */
  get canPrev() {
    return this.state.has(STATES.CAN_PREV);
  }
  
  /**
   * Can scroll to next position
   * @type {boolean}
   */
  get canNext() {
    return this.state.has(STATES.CAN_NEXT);
  }
  
  /**
   * Whether scroll is active (content overflows)
   * @type {boolean}
   */
  get isActive() {
    return this.state.has(STATES.ACTIVE);
  }
  
  /**
   * Whether scroll is disabled
   * @type {boolean}
   */
  get isDisabled() {
    return this.#isDisabled;
  }
  
  /**
   * Current scroll axis
   * @type {'x' | 'y'}
   */
  get axis() {
    return this.#axis;
  }
  
  /**
   * Maximum scroll distance in pixels
   * @type {number}
   */
  get maxScroll() {
    return this.#axis === 'y' ? this.#maxY : this.#maxX;
  }
}

/* ═══════════════════════════════════════════════════════════════════
 * REGISTER WEB COMPONENT
 * ═══════════════════════════════════════════════════════════════════ */

customElements.define('liftr-scroll', LiftrScroll);

/* ═══════════════════════════════════════════════════════════════════
 * EXPORTS
 * ═══════════════════════════════════════════════════════════════════ */

export { LiftrScroll, ROLES, STATES, EVENTS, SETTINGS, PROPERTIES };